import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CreateQrdaReportRequest, QrdaExportClient, QrdaReportClient, QrdaReportRunsSearchDto } from '@medone/medonehp-api-client';

import { handleError } from '../../../shared/common/HandleErrors';
import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import { initialReportBundleState, ReportBundleState } from './report-models';

const buildClient = () => new QrdaReportClient(null as any, Axios);

export const fetchReports = createAsyncThunk('qualityReportingReport/fetchReports', async (search: Partial<QrdaReportRunsSearchDto> & { current?: number; pageSize?: number }) => {
    const dto = QrdaReportRunsSearchDto.fromJS({
        current: search.current ?? 1,
        pageSize: search.pageSize ?? 50,
        status: search.status,
        createdByMe: search.createdByMe,
    });
    const response = await buildClient().getReports(dto);
    if (response.result.succeeded) {
        return {
            items: response.result.entity ?? [],
            total: response.result.pagination?.total ?? response.result.entity?.length ?? 0,
        };
    }
    handleError(response, () => {});
    return { items: [], total: 0 };
});

export const fetchReport = createAsyncThunk('qualityReportingReport/fetchReport', async (id: number) => {
    const response = await buildClient().getReport(id);
    if (response.result.succeeded) {
        return response.result.entity ?? null;
    }
    handleError(response, () => {});
    return null;
});

/**
 * Polls the report's Cat I child run (by id, via the existing Cat I getRun) for live per-patient
 * progress while the report is still generating. Silent on failure — it's background polling.
 */
export const fetchCatIProgress = createAsyncThunk('qualityReportingReport/fetchCatIProgress', async (catIRunId: number) => {
    const response = await new QrdaExportClient(null as any, Axios).getRun(catIRunId);
    return response.result.succeeded ? (response.result.entity ?? null) : null;
});

export const createReport = createAsyncThunk('qualityReportingReport/createReport', async (req: CreateQrdaReportRequest) => {
    const response = await buildClient().createReport(req);
    if (response.result.succeeded) {
        return response.result.entity!;
    }
    handleError(response, () => {});
    throw new Error(response.result.message || 'Failed to create report');
});

/**
 * Re-enqueues a report stuck in NotStarted/Running (>= 1 min old). Raw Axios call against the
 * Retrigger endpoint — the generated QrdaReportClient gains a `retrigger` method on the next NSwag
 * regen, after which this can switch to it. Surfaces the server's 404/409 message on failure.
 */
export const retriggerReport = createAsyncThunk('qualityReportingReport/retriggerReport', async (id: number) => {
    // The shared Axios instance has no baseURL and its request interceptor runs `new URL(config.url)`,
    // so the URL must be absolute. Reuse the generated client's env-aware base resolution instead of a
    // relative path. Switch to the typed `QrdaReportClient.retrigger` once NSwag is regenerated.
    const baseUrl = (buildClient() as any).getBaseUrl('');
    try {
        const response = await Axios.post(`${baseUrl}/api/QrdaReport/Retrigger/${id}`);
        return response.data?.entity ?? null;
    } catch (e: any) {
        throw new Error(e?.response?.data?.title || e?.message || 'Retry failed.');
    }
});

export interface BundleBlob {
    data: Blob;
    fileName?: string;
    headers?: Record<string, string>;
}

/**
 * Fetches the bundle ZIP for a report and returns the raw Blob (plus filename/headers) WITHOUT
 * forcing a download. Shared by `downloadBundle` and the in-browser artifact viewer/browser
 * (see lib/bundle-cache). The returned bytes are PHI — hold them in memory only, never log them.
 */
export const fetchBundleBlob = async (id: number): Promise<BundleBlob | null> => {
    const response = await buildClient().downloadBundle(id);
    const file = response.result;
    return file?.data ? { data: file.data, fileName: file.fileName, headers: file.headers } : null;
};

/**
 * Streams the bundle ZIP for a completed report and forces a browser download. Mirrors the
 * blob + anchor-click pattern used by the Cat I export slice.
 */
export const downloadBundle = createAsyncThunk('qualityReportingReport/downloadBundle', async (id: number) => {
    const file = await fetchBundleBlob(id);
    if (!file) return;

    const contentType = (file.headers && (file.headers['content-type'] || file.headers['Content-Type'])) || 'application/zip';
    const blob = new Blob([file.data], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', file.fileName || `qrda-report-${id}.zip`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
});

/**
 * Hard-deletes a completed or failed report and everything it owns (Cat I run, Cat III run,
 * per-patient rows, generated files) on the backend. Raw Axios call — the generated QrdaReportClient
 * gains a typed `delete` on the next NSwag regen. Surfaces the server's 404/409 message on failure.
 */
export const deleteReport = createAsyncThunk('qualityReportingReport/deleteReport', async (id: number) => {
    const baseUrl = (buildClient() as any).getBaseUrl('');
    try {
        await Axios.delete(`${baseUrl}/api/QrdaReport/Delete/${id}`);
        return id;
    } catch (e: any) {
        throw new Error(e?.response?.data?.title || e?.message || 'Delete failed.');
    }
});

const slice = createSlice({
    name: 'qualityReportingReport',
    initialState: initialReportBundleState,
    reducers: {
        clearCurrentReport: (state: ReportBundleState) => {
            state.currentRun = null;
            state.catIProgress = null;
        },
        setReportError: (state: ReportBundleState, action: PayloadAction<string | null>) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReports.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchReports.fulfilled, (state, action) => {
                state.loading = false;
                state.runs = action.payload.items;
                state.runsTotal = action.payload.total;
            })
            .addCase(fetchReports.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load reports.';
            })
            .addCase(fetchReport.fulfilled, (state, action) => {
                state.currentRun = action.payload;
            })
            .addCase(fetchCatIProgress.fulfilled, (state, action) => {
                state.catIProgress = action.payload;
            })
            .addCase(createReport.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
                state.catIProgress = null;
            })
            .addCase(createReport.fulfilled, (state, action) => {
                state.processing = false;
                state.currentRun = action.payload;
            })
            .addCase(createReport.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Failed to create report.';
            })
            .addCase(deleteReport.fulfilled, (state, action) => {
                state.runs = state.runs.filter((r) => r.id !== action.payload);
                state.runsTotal = Math.max(0, state.runsTotal - 1);
            });
    },
});

export const { clearCurrentReport, setReportError } = slice.actions;

export const selectReportRuns = (state: RootState) => (state as any).qualityReportingReport?.runs ?? [];
export const selectReportRunsTotal = (state: RootState) => (state as any).qualityReportingReport?.runsTotal ?? 0;
export const selectCurrentReport = (state: RootState) => (state as any).qualityReportingReport?.currentRun ?? null;
export const selectReportLoading = (state: RootState) => (state as any).qualityReportingReport?.loading ?? false;
export const selectReportProcessing = (state: RootState) => (state as any).qualityReportingReport?.processing ?? false;
export const selectReportError = (state: RootState) => (state as any).qualityReportingReport?.errorMessage ?? null;
export const selectCatIProgress = (state: RootState) => (state as any).qualityReportingReport?.catIProgress ?? null;

export default slice.reducer;
